import React, { useEffect, useRef, useState } from 'react';

import { Button, Input } from '@copilot/ui';
import { Field } from '../common/Field';
import { useAuth } from '../../hooks/useAuth';
import {
  CONTACT_LIMITS,
  CONTACT_REASONS,
  sendContactMessage,
} from '../../services/contactApi';

// The Contact Us form: name, email and a message. It lives in the
// landing page's #contact section; pages/ContactPage.jsx shows the same
// form to a signed-in user, who never sees the landing page.
//
// Needs no account. A session, when there is one, only prefills the
// name and email.

const CUSTOM_PLAN_REASON = 'custom_plan';

// One fixed message for every failure. What actually went wrong (mail
// switched off, the SMTP server refusing) is for the server's logs, not
// for the page.
const FAILURE_MESSAGE =
  "We couldn't send your message right now. Please try again shortly.";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// "custom-plan" (as written in a URL) -> custom_plan. Anything
// unrecognised, or nothing, is a general inquiry rather than an error.
function normaliseReason(raw) {
  const value = String(raw || '').trim().toLowerCase().replace(/-/g, '_');
  return CONTACT_REASONS.includes(value) ? value : 'general';
}

function validate(form) {
  const errors = {};

  if (!form.name.trim()) errors.name = 'Enter your name.';

  if (!form.email.trim()) errors.email = 'Enter your email address.';
  else if (!EMAIL_PATTERN.test(form.email.trim())) {
    errors.email = 'Enter a valid email address.';
  }

  const message = form.message.trim();
  if (!message) errors.message = 'Enter a message.';
  else if (message.length < CONTACT_LIMITS.messageMin) {
    errors.message = `Your message must be at least ${CONTACT_LIMITS.messageMin} characters.`;
  }

  return errors;
}

// In page order, so focus lands on the first field that needs fixing.
const FIELD_IDS = {
  name: 'contact-name',
  email: 'contact-email',
  message: 'contact-message',
};

// `reason` is the raw ?reason= value from the URL, if any. There is no
// reason field to fill in: the pricing Custom card's link says
// custom-plan, and everything else is a general inquiry. It decides the
// subject of the email the team receives and the hint under Message.
function ContactForm({ reason }) {
  const { user } = useAuth();
  const sendReason = normaliseReason(reason);
  const isCustomPlan = sendReason === CUSTOM_PLAN_REASON;

  const [form, setForm] = useState(() => ({
    name: user?.full_name || '',
    email: user?.email || '',
    message: '',
  }));
  const [errors, setErrors] = useState({});
  const [failed, setFailed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // The reason the delivered message was sent with - null until one is.
  const [sentReason, setSentReason] = useState(null);
  // State updates are asynchronous; a ref is what actually stops a
  // second submit fired before the button re-renders as disabled.
  const submittingRef = useRef(false);
  const successRef = useRef(null);

  // The session may still be loading when the form first renders. Fill
  // the name and email once it arrives, without overwriting anything
  // already typed.
  useEffect(() => {
    if (!user) return;
    setForm((current) => ({
      ...current,
      name: current.name || user.full_name || '',
      email: current.email || user.email || '',
    }));
  }, [user]);

  useEffect(() => {
    if (sentReason) successRef.current?.focus();
  }, [sentReason]);

  const setField = (field) => (event) => {
    const { value } = event.target;
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => (current[field] ? { ...current, [field]: undefined } : current));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submittingRef.current) return;

    const found = validate(form);
    setErrors(found);
    setFailed(false);

    const firstInvalid = Object.keys(FIELD_IDS).find((field) => found[field]);
    if (firstInvalid) {
      document.getElementById(FIELD_IDS[firstInvalid])?.focus();
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);

    try {
      await sendContactMessage({
        name: form.name.trim(),
        email: form.email.trim(),
        reason: sendReason,
        message: form.message.trim(),
      });
      setSentReason(sendReason);
    } catch (_) {
      // Never the backend's own text - see FAILURE_MESSAGE.
      setFailed(true);
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  // A deliberate fresh start: who they are is kept, what they said is not.
  const startNewMessage = () => {
    setForm((current) => ({ ...current, message: '' }));
    setErrors({});
    setFailed(false);
    setSentReason(null);
  };

  if (sentReason) {
    return (
      <div className="contact-success" role="status" tabIndex={-1} ref={successRef}>
        <h3>
          {sentReason === CUSTOM_PLAN_REASON
            ? 'Thanks for your interest in a Custom plan.'
            : 'Thanks for contacting us.'}
        </h3>
        <p>
          {sentReason === CUSTOM_PLAN_REASON
            ? "We'll review your requirements and contact you shortly."
            : "We've received your message and will get back to you shortly."}
        </p>
        <div>
          <Button variant="secondary" onClick={startNewMessage}>
            Send another message
          </Button>
        </div>
      </div>
    );
  }

  const messageDescription = errors.message
    ? `${FIELD_IDS.message}-error`
    : isCustomPlan
      ? `${FIELD_IDS.message}-help`
      : undefined;

  return (
    <form className="auth-form contact-form" onSubmit={handleSubmit} noValidate>
      <Field label="Name" htmlFor={FIELD_IDS.name}>
        <Input
          id={FIELD_IDS.name}
          type="text"
          autoComplete="name"
          maxLength={CONTACT_LIMITS.nameMax}
          value={form.name}
          onChange={setField('name')}
          error={errors.name}
          required
        />
      </Field>

      <Field label="Email" htmlFor={FIELD_IDS.email}>
        <Input
          id={FIELD_IDS.email}
          type="email"
          autoComplete="email"
          value={form.email}
          onChange={setField('email')}
          error={errors.email}
          required
        />
      </Field>

      {/* Name and Email share a row; the message spans both. */}
      <div className="contact-form-full">
        <Field label="Message" htmlFor={FIELD_IDS.message}>
          <textarea
            id={FIELD_IDS.message}
            className={`input${errors.message ? ' input-invalid' : ''}`}
            rows={4}
            maxLength={CONTACT_LIMITS.messageMax}
            value={form.message}
            onChange={setField('message')}
            aria-invalid={errors.message ? true : undefined}
            aria-describedby={messageDescription}
            required
          />
          {errors.message && (
            <span className="field-error" id={`${FIELD_IDS.message}-error`}>
              {errors.message}
            </span>
          )}
          {/* With no separate fields for it, this is where a Custom plan
              request says how much it needs. */}
          {isCustomPlan && !errors.message && (
            <p className="field-help" id={`${FIELD_IDS.message}-help`}>
              Asking about a Custom plan? Tell us roughly how many meetings
              you need each month.
            </p>
          )}
        </Field>
      </div>

      {failed && (
        <div className="form-alert" role="alert">
          {FAILURE_MESSAGE}
        </div>
      )}

      <Button type="submit" block disabled={isSubmitting} aria-busy={isSubmitting || undefined}>
        {isSubmitting ? 'Sending...' : 'Send Message'}
      </Button>
    </form>
  );
}

export default ContactForm;
